import { Injectable, OnDestroy } from '@angular/core';
import { MessageHttpService } from './message.http.service';
import { IBase, IMessage } from 'src/app/models';
import { CommunicationUserType, MessageStatus } from 'src/app/models/enums';
import { BehaviorSubject, lastValueFrom, Subscription, interval } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from 'src/app/store/initialisation/pagination';
import { IPushNotification, IPushNotificationResponse } from 'src/app/models/pushNotification';

@Injectable({
  providedIn: 'root',
})
export class MessageService implements OnDestroy {
  private pollingSubscription: Subscription | null = null;
  private readonly POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

  private notificationsSubject = new BehaviorSubject<IPushNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private messageHttpService: MessageHttpService) {}

  ngOnDestroy() {
    this.stopPolling();
  }

  startPolling() {
    this.fetchPushNotifications();

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchPushNotifications();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  async fetchPushNotifications(page: number = 1, size: number = 20) {
    this.loadingSubject.next(true);
    try {
      const source$ = this.messageHttpService.getPushNotifications(page, size);
      const response = await lastValueFrom(source$);
      const data = objectToCamel(response as object) as { data: IPushNotificationResponse };
      const notifications = data.data?.content || [];
      this.notificationsSubject.next(notifications);
      this.updateUnreadCount(notifications);
      return notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private updateUnreadCount(notifications: IPushNotification[]) {
    const unreadCount = notifications.filter(n => !n.read).length;
    this.unreadCountSubject.next(unreadCount);
  }

  async markPushNotificationAsRead(id: number) {
    try {
      const source$ = this.messageHttpService.markPushNotificationAsRead(id);
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      this.notificationsSubject.next(updatedNotifications);
      this.updateUnreadCount(updatedNotifications);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  async markPushNotificationAsUnread(id: number) {
    try {
      const source$ = this.messageHttpService.markPushNotificationAsUnread(id);
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === id ? { ...n, read: false } : n
      );
      this.notificationsSubject.next(updatedNotifications);
      this.updateUnreadCount(updatedNotifications);
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  }

  async togglePushNotificationReadStatus(notification: IPushNotification) {
    if (notification.read) {
      await this.markPushNotificationAsUnread(notification.id);
    } else {
      await this.markPushNotificationAsRead(notification.id);
    }
  }

  async markAllPushNotificationsAsRead() {
    try {
      const source$ = this.messageHttpService.markAllPushNotificationsAsRead();
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n => ({ ...n, read: true }));
      this.notificationsSubject.next(updatedNotifications);
      this.unreadCountSubject.next(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  getPushNotifications() {
    return this.notificationsSubject.value;
  }

  getUnreadCount() {
    return this.unreadCountSubject.value;
  }

  async createMessage(message: IMessage, userType: CommunicationUserType) {
    const source$ = this.messageHttpService.createMessage(message, userType);
    const response = await lastValueFrom(source$);
    const messageCreated = objectToCamel(response as object);
    return messageCreated;
  }

  async getMessages(type: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.messageHttpService.getMessages(
      type,
      page,
      size
    );
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getData(messages);
    return messagesArray;
  }

  async filterMessages(
    communicationUserType: CommunicationUserType,
    page: number,
    size: number,
    clientIds: IBase[],
    senderIds: IBase[],
    receiverIds: IBase[],
    searchValue?: string
  ) {
    const source$ = this.messageHttpService.filter(
      communicationUserType,
      page,
      size,
      clientIds,
      senderIds,
      receiverIds,
      searchValue
    );
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getData(messages);
    return messagesArray;
  }

  async getSenders(userType: CommunicationUserType) {
    const source$ = this.messageHttpService.getSenders(userType);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    return messages;
  }

  async getMessageDetails(id: number, page: number, size: number, status: MessageStatus, searchValue?: string) {
    const source$ = this.messageHttpService.getMessageDetail(id, page, size, status, searchValue);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getDataWithPagination(messages, page);
    return messagesArray;
  }

  async getMessageReceivers(message: IMessage, userType: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.messageHttpService.getMessageReceivers(message, userType, page, size);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getDataWithPagination(messages, page || 0);
    return messagesArray;
  }

  getData(response: any) {
    return response.data;
  }

  getDataWithPagination(response: any, page: number) {
    const data = response.data ?? response;
    return {
      value: data.content,
      pagination: {
        ...initialPaginationState,
        numberOfElements: data.numberOfElements,
        totalPages: data.totalPages,
        totalElements: data.totalElements,
        first: data.first,
        last: data.last,
        actualPage: page,
        size: data.size
      }
    };
  }
}
